import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../store/AppProvider";
import {
  addRetailer,
  deletRetailer,
  editRetailer,
  exportRetailer,
  getRetailersAndCountryCode,
} from "../../service/Service";
import { Button, LinearProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  SHOW_ERROR_ALERT,
  SHOW_SUCCESS_ALERT,
} from "../../store/reducer/AlertNotifierReducer";
import { FEATURES } from "../../utils/Utils";
import RetailerTable from "../../component/RetailerComponents/RetailerTable";
import RetailerFormModal from "../../component/RetailerComponents/RetailerFormModal";
import ConfirmDeleteDialog from "../../component/RetailerComponents/ConfirmDeleteDialog";

export const ManageRetailers = () => {
  const { setAlert, setShowLoader, showLoader } = useContext(AppContext);
  const [retailerList, setRetailerList] = useState([]);
  const [availableCountryCodes, setAvailableCountryCodes] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const fetchRetailers = useCallback(async () => {
    try {
      setShowLoader(true);
      setRetailerList([]);
      setAvailableCountryCodes([]);

      const response = await getRetailersAndCountryCode();
      setRetailerList(response.retailers);
      setAvailableCountryCodes(response.country_codes);
      // console.log(retailerList);
    } catch (error) {
      setAlert({
        type: SHOW_ERROR_ALERT,
        alertMessage: error,
      });
    } finally {
      setShowLoader(false);
    }
  }, [setAlert, setShowLoader]);

  useEffect(() => {
    fetchRetailers();
    return () => {
      setShowLoader(false);
    };
  }, [fetchRetailers, setShowLoader]);

  const handleSave = async (formData) => {
    try {
      setShowLoader(true);
      let response;
      if (selectedRetailer) {
        // save the retailer data
        response = await editRetailer(formData);
      } else {
        // add the retailer data
        response = await addRetailer(formData);
      }
      setAlert({
        type: SHOW_SUCCESS_ALERT,
        alertMessage: `Successfully ${selectedRetailer ? "updated" : "saved"} Retailer "${response?.retailer_name}" under ID:${response?.retailer_id}!`,
      });
      setIsFormOpen(false);
      fetchRetailers();
    } catch (error) {
      setAlert({
        type: SHOW_ERROR_ALERT,
        alertMessage: error,
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setShowLoader(true);
      const { retailer_id } = deleteTarget;
      const response = await deletRetailer({ retailer_id });
      fetchRetailers();
      setAlert({
        type: SHOW_SUCCESS_ALERT,
        alertMessage: `Retailer ${response?.retailer_name}(ID: ${response?.retailer_id}) was deleted successfully!`,
      });
    } catch (error) {
      setAlert({
        type: SHOW_ERROR_ALERT,
        alertMessage: error,
      });
    } finally {
      setShowLoader(false);
      setDeleteTarget(null);
    }
  };

  const handleExportRetailerData = async (column) => {
    try {
      const fetchedData = await exportRetailer();
      window.open(fetchedData, "_blank");
      setAlert({
        type: SHOW_SUCCESS_ALERT,
        alertMessage: "CSV Download successful!",
      });
    } catch (e) {
      setAlert({
        type: SHOW_ERROR_ALERT,
        alertMessage: `Failed To Export Retailers Data`,
      });
    }
  };

  return (
    <div>
      <div className="header-title" style={{ letterSpacing: "0.5px" }}>
        MANAGE RETAILERS
      </div>

      <div style={{ display: "flex", justifyContent: "end", padding: "1rem" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            textTransform: "none",
            size: "large",
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            py: 1,
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "#1565c0",
              boxShadow: 4,
            },
          }}
          onClick={() => {
            setSelectedRetailer(null);
            setIsFormOpen(true);
          }}
        >
          Add Retailer
        </Button>
      </div>
      {/* Table displaying the full list with client-side pagination */}
      {showLoader && <LinearProgress />}
      <RetailerTable
        data={retailerList}
        loading={showLoader}
        FEATURES={FEATURES}
        onEdit={(retailer) => {
          setSelectedRetailer(retailer);
          setIsFormOpen(true);
        }}
        onDelete={(retailer) => setDeleteTarget(retailer)}
        handleExportRetailerData={handleExportRetailerData}
      />

      {/* Add / Edit Form Modal */}
      <RetailerFormModal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSave}
        initialData={selectedRetailer}
        existingRetailers={retailerList}
        countryCodes={availableCountryCodes}
      />

      {/* Confirmation Dialog befor Delete */}
      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        retailerName={deleteTarget?.retailer_name || ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};
